import axios from 'axios';
import { clearAuthClientSession } from '../features/auth/tokenCookie';

const trimTrailingSlashes = (value: string): string => value.replace(/\/+$/, '');

const shouldReplaceLocalhostUrl = (value: string): boolean => {
    if (typeof window === 'undefined') return false;
    const frontendHost = window.location.hostname;
    if (!frontendHost || frontendHost === 'localhost' || frontendHost === '127.0.0.1') return false;

    try {
        const parsed = new URL(value);
        return parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    } catch (_error) {
        return false;
    }
};

const shouldReplaceInsecureUrl = (value: string): boolean => {
    if (typeof window === 'undefined' || window.location.protocol !== 'https:') return false;

    try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:';
    } catch (_error) {
        return false;
    }
};

const getWindowHostApiBaseUrl = (): string | null => {
    if (typeof window === 'undefined') return null;
    return `${window.location.origin}/api`;
};

const resolveApiBaseUrl = (): string => {
    const fromEnv = process.env.REACT_APP_API_BASE_URL;
    if (typeof fromEnv === 'string' && fromEnv.trim()) {
        const normalizedEnv = trimTrailingSlashes(fromEnv.trim());
        if (shouldReplaceLocalhostUrl(normalizedEnv) || shouldReplaceInsecureUrl(normalizedEnv)) {
            return getWindowHostApiBaseUrl() || normalizedEnv;
        }
        return normalizedEnv;
    }

    return getWindowHostApiBaseUrl() || '/api';
};

const resolveApiTimeoutMs = (): number => {
    const parsed = Number(process.env.REACT_APP_API_TIMEOUT_MS);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 20000;
};

const axiosInstance = axios.create({
    baseURL: resolveApiBaseUrl(),
    withCredentials: true,
    timeout: resolveApiTimeoutMs(),
});

axiosInstance.interceptors.request.use((config) => {
    if (config.data instanceof FormData && config.headers) {
        const deleteContentType = (headers: any) => {
            if (!headers) return;
            if (typeof headers.delete === 'function') {
                headers.delete('content-type');
                headers.delete('Content-Type');
            }
            delete headers['Content-Type'];
            delete headers['content-type'];
        };

        deleteContentType(config.headers);
        deleteContentType((config.headers as any).common);
        deleteContentType((config.headers as any).post);
        deleteContentType((config.headers as any).put);
        deleteContentType((config.headers as any).patch);
    }
    return config;
});

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = Number(error?.response?.status || 0);
        if ((status === 401 || status === 403) && typeof window !== 'undefined') {
            clearAuthClientSession();
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;
